import get from 'lodash/get';
import Immutable from 'immutable';
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import ls from 'local-storage';

import { Message, User } from 'models';
import lockReasons from 'globals/lockReasons';
import { loadCookie, saveCookie, removeCookie } from 'globals/cookieHelper';

import AppAPI, { DEFAULT_MESSAGES_PAGE_SIZE } from '../../../../api';
import * as utils from '../../../../globals/utils';
import { onSocketMessage } from '../../../../api/connect/socket/actions';

import {
  getActiveSessionId,
  chatConversationIsOpen,
  getActiveSession,
  getChatForm,
  selectIsAfterHours,
  getCompany,
  getActiveSessionUsers,
  hasAgentInActiveSessionMessages,
  getMostRecentChatId,
  selectActiveSessionHasCampaign,
  sessionContainsCampaign,
  hasMoreThanOneActiveSession,
  getCampaignMessages,
  getTotalUnreadMessageCount,
  getUpfrontVolumeControlEnabled,
  isSessionLocked,
  selectSessionWaitTime,
  getUpfrontVolumeControlThreshold,
  getSessionStates,
  makeGetSatisfactionFormByResponseId,
  makeGetSessionBySessionId,
} from './selectors';

import * as routingActions from './actions_routing';
import * as volumeControlActions from '../../conversation/ChatConversationVolumeControlInput/actions';
import * as appActions from '../../../app/App/actions';
import * as inputActions from '../../conversation/ChatConversationInput/actions';
import { responseToSession } from '../../../../api/views/session';
import * as vcTimerHelpers from '../../conversation/ChatConversationVolumeControlInput/helpers';
import * as helpers from './helpers';

let sessionStateLoaded = false;

const DEFAULT_OBJECT = {};

export const CHAT_UPDATE_USERS = 'chat-update-users';
export const CHAT_UPDATE_COMPANY = 'chat-update-company';
export const CHAT_UPDATE_COMPANY_HOURS = 'chat-update-company-hours';
export const CHAT_UPDATE_COMPANY_HOLIDAYS = 'chat-update-company-holidays';
export const CHAT_UPDATE_SESSION = 'chat-update-session';
export const CHAT_MODIFY_SESSION = 'chat-modify-session';
export const CHAT_UPDATE_SESSIONS = 'chat-update-sessions';
export const CHAT_UPDATE_SESSION_MESSAGES = 'chat-update-session-messages';
export const CHAT_SCROLL_TO_MESSAGE_ID = 'chat-scroll-to-message-id';
export const CHAT_RESET_SCROLL_TO_MESSAGE_ID = 'chat-reset-scroll-to-message-id';
export const CHAT_NEW_SESSION = 'chat-new-session';
export const CHAT_FORM_INIT = 'CHAT_FORM_INIT';
export const CHAT_FORM_POST = 'CHAT_FORM_POST';
export const CHAT_FORM_GET_TEAMS = 'CHAT_FORM_GET_TEAMS';
export const CHAT_FORM_IN_PROGRESS = 'CHAT_FORM_IN_PROGRESS';
export const CHAT_IS_AFTER_HOURS = 'CHAT_IS_AFTER_HOURS';
export const CHAT_VOLUME_CONTROL_START = 'CHAT_VOLUME_CONTROL_START';
export const CHAT_VOLUME_CONTROL_STOP = 'CHAT_VOLUME_CONTROL_STOP';
export const CHAT_VOLUME_CONTROL_POST = 'CHAT_VOLUME_CONTROL_POST';
export const CHAT_ATTACHMENT_CREATE = 'CHAT_ATTACHMENT_CREATE';
export const CHAT_ATTACHMENT_FETCH = 'CHAT_ATTACHMENT_FETCH';
export const INIT_SINGLE_CHAT = 'INIT_SINGLE_CHAT';
export const UPDATE_SINGLE_CHAT = 'UPDATE_SINGLE_CHAT';
export const CHAT_QUEUE_FETCH = 'CHAT_QUEUE_FETCH';
export const CHAT_QUEUE_POLLING_CLEAR = 'CHAT_QUEUE_POLLING_CLEAR';
export const LAST_ACTIVITY = 'LAST_ACTIVITY';
export const CHAT_MESSAGE_RECEIVED = 'chat-message-received';
export const SESSION_STATE = 'SESSION_STATE';
export const SATISFACTION_FORM = 'SATISFACTION_FORM';
export const SET_FOCUS = 'SET_FOCUS';
export const TOGGLE_CAMPAIGN_CLICKED = 'TOGGLE_CAMPAIGN_CLICKED';

export const chatUpdateUsers = users => {
  return { type: CHAT_UPDATE_USERS, users };
};

export const chatUpdateCompany = company => {
  return { type: CHAT_UPDATE_COMPANY, company };
};

export const chatUpdateCompanyHours = hours => {
  return { type: CHAT_UPDATE_COMPANY_HOURS, hours };
};

export const chatUpdateCompanyHolidays = holidays => {
  return { type: CHAT_UPDATE_COMPANY_HOLIDAYS, holidays };
};

export const chatUpdateSessions = sessions => {
  return { type: CHAT_UPDATE_SESSIONS, sessions };
};

export const chatUpdateSession = session => {
  return { type: CHAT_UPDATE_SESSION, session };
};

export const chatModifySession = session => {
  return { type: CHAT_MODIFY_SESSION, session };
};

export const chatUpdateSessionMessages = (sessionId, messages) => {
  return { type: CHAT_UPDATE_SESSION_MESSAGES, sessionId, messages };
};

export const chatInitForm = form => ({
  type: CHAT_FORM_INIT,
  payload: { form },
});

export const chatVolumeControlStart = sessionId => ({
  type: CHAT_VOLUME_CONTROL_START,
  sessionId,
});

export const chatVolumeControlStop = sessionId => ({
  type: CHAT_VOLUME_CONTROL_STOP,
  sessionId,
});

export const handleChatFormTeams = teams => ({
  type: CHAT_FORM_GET_TEAMS,
  payload: { teams },
});

export const chatNewSession = session => {
  saveCookie('sessionId', session.id);
  return { type: CHAT_NEW_SESSION, session };
};

export const chatResetScrollToMessageId = () => {
  return { type: CHAT_RESET_SCROLL_TO_MESSAGE_ID };
};

export const chatScrollToMessageId = messageId => {
  return { type: CHAT_SCROLL_TO_MESSAGE_ID, messageId };
};

export const chatIsAfterHours = isAfterHours => {
  return {
    type: CHAT_IS_AFTER_HOURS,
    isAfterHours,
  };
};

export const chatFormPost = form => ({
  type: CHAT_FORM_POST,
  form,
});

export const chatVolumeControlPost = form => {
  return {
    type: CHAT_VOLUME_CONTROL_POST,
    form,
  };
};

export const chatAttachmentCreate = attachment => {
  return {
    type: CHAT_ATTACHMENT_CREATE,
    attachment,
  };
};

export const chatAttachmentFetch = (attachment, messageId) => {
  return {
    type: CHAT_ATTACHMENT_FETCH,
    attachment,
    messageId,
  };
};

export const chatQueueFetch = sessionQueue => {
  return {
    type: CHAT_QUEUE_FETCH,
    sessionQueue,
  };
};

export const clearSessionQueueEntities = () => {
  return {
    type: CHAT_QUEUE_POLLING_CLEAR,
  };
};

export const updateLastActivity = activity => {
  return {
    type: LAST_ACTIVITY,
    activity,
  };
};

export const updateSocketActivity = (eventName, data) => {
  return {
    type: eventName,
    data,
  };
};

export const onMessageReceived = message => {
  return { type: CHAT_MESSAGE_RECEIVED, message };
};

export const setSessionState = sessionStates => {
  return { type: SESSION_STATE, sessionStates };
};

export const addSatisfactionForm = satisfactionForm => {
  return { type: SATISFACTION_FORM, satisfactionForm };
};

export const toggleCampaignClicked = data => ({ type: TOGGLE_CAMPAIGN_CLICKED, data });

export const handleMarkChatSessionSeen = sessionId => {
  return dispatch => {
    if (sessionId && sessionId !== 'new') {
      AppAPI.markChatSessionSeen(sessionId).then(session => {
        return dispatch(chatUpdateSession(session));
      });
    }
  };
};

export const handleChatFetchUserId = userId => {
  return dispatch => {
    AppAPI.getUser(userId).then(users => {
      dispatch(chatUpdateUsers([users]));
    });
  };
};

export const handleChatFetchUserIds = userIds => {
  return dispatch => {
    AppAPI.getUsers(userIds).then(users => {
      dispatch(chatUpdateUsers(users));
    });
  };
};

const handleChatFetchUsersInMessages = messages => {
  return dispatch => {
    const userIds = messages.reduce((list, message) => {
      if (message.sentBy.type !== 'user' || list.includes(message.sentBy.id)) return list;
      list.push(message.sentBy.id);
      return list;
    }, []);

    if (userIds && userIds.length > 0) {
      dispatch(handleChatFetchUserIds(userIds));
    }
  };
};

export const getSatisfactionForm = sessionId => {
  return dispatch => {
    AppAPI.getSatisfactionForm(sessionId).then(({ satisfactionForm, session }) => {
      dispatch(addSatisfactionForm(satisfactionForm));
      dispatch(chatModifySession(Object.assign({}, { id: sessionId }, session)));
    });
  };
};

export const maybeGetSatisfactionForm = sessionId => {
  const getSatisfactionFormByResponseId = makeGetSatisfactionFormByResponseId();
  const getSessionBySessionId = makeGetSessionBySessionId();

  return (dispatch, getState) => {
    const state = getState();

    const session = getSessionBySessionId(state, sessionId);
    const responseId = session.get('satisfactionId');

    const form = getSatisfactionFormByResponseId(state, responseId);

    if (!form.get('id')) {
      dispatch(getSatisfactionForm(sessionId));
    }
  };
};

export const maybeGetSatisfactionFormIfAgentJoined = (sessionId, lockReason) => {
  return (dispatch, getState) => {
    // Only want customer or user ended events to trigger satisfaction
    if (!['customer_ended', 'user_ended'].includes(lockReason)) return;

    const state = getState();
    const sessionUsers = getActiveSessionUsers(state);

    // Only fetch satisfaction if an agent joined the conversation. Ignore system users.
    if (sessionUsers && sessionUsers.size > 0 && sessionUsers.some(user => user.id !== '__team')) {
      dispatch(getSatisfactionForm(sessionId));
    }
  };
};

export const handleMaybeUnblockingInput = ({ sessionId, isSocket }) => (dispatch, getState) => {
  const disabledInputCookie = loadCookie(`input-disabled-${sessionId}`);
  if (!disabledInputCookie || !sessionId) return;
  if (isSocket) {
    removeCookie(`input-disabled-${sessionId}`);
    return;
  }
  const sessionContainsAgentResponse = hasAgentInActiveSessionMessages(getState());
  if (sessionContainsAgentResponse) {
    removeCookie(`input-disabled-${sessionId}`);
  }
};

export const handleMaybeClearQueues = () => (dispatch, getState) => {
  const state = getState();
  const sessionContainsAgentResponse = hasAgentInActiveSessionMessages(state);
  const upfrontEnabled = getUpfrontVolumeControlEnabled(state);

  if (upfrontEnabled && sessionContainsAgentResponse) {
    dispatch(clearSessionQueueEntities());
  }
};

export const markConversationDone = (sessionId, lockReason) => {
  return async dispatch => {
    try {
      const session = await AppAPI.markConversationDone(sessionId, lockReason);
      dispatch(chatUpdateSession(session));
      dispatch(clearSessionQueueEntities());
      return session;
    } catch (e) {
      return Promise.reject(e);
    }
  };
};

export const initSingleChat = activeSessionId => {
  return {
    type: INIT_SINGLE_CHAT,
    activeSessionId,
  };
};

export const updateSingleChat = activeSessionId => {
  return {
    type: UPDATE_SINGLE_CHAT,
    activeSessionId,
  };
};

export const maybeDismissCampaigns = () => (dispatch, getState) => {
  const state = getState();
  const settings = getCompany(state);

  if (!settings.singleSessionChat) return;

  const campaignMessages = getCampaignMessages(state) || Immutable.OrderedSet();

  // toJS ;_;
  const uniqSessionMessages = uniqBy(campaignMessages.toJS(), message => message.sessionId);

  uniqSessionMessages.forEach(message => {
    const sessionId = message.sessionId;

    if (sessionId) {
      dispatch(handleMarkChatSessionSeen(sessionId));
      dispatch(markConversationDone(sessionId, lockReasons.CUSTOMER_ENDED));
    }
  });
};

export const handleSingleChat = () => (dispatch, getState) => {
  const state = getState();
  const activeSessionId = getMostRecentChatId(state);
  const sessionHasCampaign = sessionContainsCampaign(state, { sessionId: activeSessionId });
  const hasMultipleActive = hasMoreThanOneActiveSession(state);
  const settings = getCompany(state);

  if (settings.singleSessionChat && (!sessionHasCampaign || hasMultipleActive)) {
    dispatch(initSingleChat(activeSessionId));

    if (!activeSessionId) {
      dispatch(routingActions.chatRouteToConversation('new'));
    }
  }
};

export const maybeUpdateSingleChat = () => (dispatch, getState) => {
  const state = getState();
  const settings = getCompany(state);

  if (!settings.singleSessionChat) return;

  const activeSessionId = getActiveSessionId(state);
  const isCampaignResponse = selectActiveSessionHasCampaign(state);

  if (isCampaignResponse) dispatch(updateSingleChat(activeSessionId));
};

export const handleChatFetchNextMessagesForActiveSession = () => {
  return (dispatch, getStore) => {
    const activeSession = getActiveSession(getStore());

    if (!activeSession.id) {
      return;
    }

    const messageCount = activeSession.messages.size;
    const nextPage = Math.ceil((messageCount + 1) / DEFAULT_MESSAGES_PAGE_SIZE);

    ((session, page) => {
      AppAPI.getMessagesForSession(session, page)
        .then(messages => {
          const sessionId = session.id;
          dispatch(chatUpdateSessionMessages(sessionId, messages));
          dispatch(handleMaybeUnblockingInput({ sessionId, isSocket: false }));
          dispatch(handleMaybeClearQueues());
          dispatch(chatScrollToMessageId(activeSession.messages.first()));
          dispatch(handleChatFetchUsersInMessages(messages));
        })
        .catch(e => {
          console.log('Error fetching messages', e); // eslint-disable-line no-console
        }); // TODO: catch
    })(activeSession, nextPage);
  };
};

export const handleAddCompanyUser = company => {
  return dispatch => {
    const companyUser = User({
      id: '__team',
      name: company.name,
      avatarUrl: company.avatarUrl,
    });

    dispatch(chatUpdateUsers([companyUser]));
  };
};

export const handleChatIsEnabled = ({ settings, isInsideBusinessHours }, cb) => {
  // TODO: find a better way to do this
  cb({ enabled: true, settings, isInsideBusinessHours });
};

export const handleChatIsNotEnabled = ({ settings, isInsideBusinessHours }, cb) => {
  // TODO: find a better way to do this
  cb({ enabled: false, settings, isInsideBusinessHours });
};

export const handleInitCustomerData = ({ withoutCache = false }) => {
  return dispatch => {
    return AppAPI.getChatSessions(withoutCache)
      .then(sessions => {
        dispatch(chatUpdateSessions(sessions));

        return AppAPI.getMessagesForMultiSession(sessions).then(sessionMessages => {
          sessionMessages.forEach(({ sessionId, messages }) => {
            if (vcTimerHelpers.sessionHasVcTimer(sessionId)) {
              const userMessageHere =
                messages &&
                messages.reduce((value, message) => {
                  return value || (message.sentBy.type === 'user' && message.sentBy.id !== '__team');
                }, false);
              if (userMessageHere) {
                vcTimerHelpers.stopVcTimerById(sessionId);
                vcTimerHelpers.removeVcTimer(sessionId);
              }
            }
            dispatch(chatUpdateSessionMessages(sessionId, messages));
            dispatch(handleChatFetchUsersInMessages(messages));
          });
          vcTimerHelpers.resumeVcTimers();

          return sessionMessages;
        });
      }) // TODO: catch
      .catch(e => {
        console.log('Error fetching multiple sessions', e); // eslint-disable-line no-console
      });
  };
};

const ensureCustomerId = () => {
  if (AppAPI.hasCustomerId()) {
    return Promise.resolve();
  }
  return AppAPI.refreshTrackingToken();
};

export const handleOnSessionEndEvent = (eventName, data) => dispatch => {
  const id = get(data, 'data.id');
  const isClipped = get(data, 'clipped');

  if (isClipped) {
    AppAPI.getChatSession(id)
      .then(session => {
        const lockReason = get(session, 'lockReason');

        dispatch(chatUpdateSession(session));
        dispatch(maybeGetSatisfactionFormIfAgentJoined(id, lockReason));
      })
      .catch(error => {
        console.log('Error fetching sessions on end', error); // eslint-disable-line no-console
      });
  } else {
    const session = responseToSession({ data });
    const lockReason = get(session, 'lockReason');

    dispatch(chatUpdateSession(session));
    dispatch(maybeGetSatisfactionFormIfAgentJoined(id, lockReason));
  }
};

export const handleOnMessage = (eventName, data) => dispatch => {
  const isTrackingEvent = eventName === 'kustomer.tracking.identity.update';
  const isSessionEndEvent = eventName === 'kustomer.app.chat.session.end';

  if (isTrackingEvent) {
    const newCustomerId = get(data, 'data.relationships.customer.data.id');
    AppAPI.customerId = newCustomerId;
  } else if (isSessionEndEvent) {
    dispatch(handleOnSessionEndEvent(eventName, data));
  } else {
    const sessionId = get(data, 'data.relationships.session.data.id');
    const messageSentByUser = get(data, 'data.relationships.sentBy.data.type') === 'user';

    dispatch(onSocketMessage(eventName, { data }));

    if (messageSentByUser) {
      dispatch(handleMaybeUnblockingInput({ sessionId, isSocket: true }));
      dispatch(handleMaybeClearQueues());
    }
  }
};

export const handleStartConnection = (lastActivity, ignoreCustomerInit) => {
  return (dispatch, getState) => {
    return AppAPI.refreshTrackingToken().then(() => {
      AppAPI.connect({
        lastActivity,
        onConnect: () => {
          if (ignoreCustomerInit) return;

          ensureCustomerId().then(() => {
            dispatch(handleInitCustomerData({}));
          });
        },
        onMessage: (eventName, data) => {
          dispatch(handleOnMessage(eventName, data));
        },
        onDisconnect: () => {
          const state = getState();
          const isAfterHours = selectIsAfterHours(state);
          const settings = getCompany(state);
          const displaySetting = settings.offhoursDisplay;
          const displaySettingIsHide = displaySetting === 'none';
          if (isAfterHours && displaySettingIsHide) {
            dispatch(appActions.stopApp());
          }
        },
      });
      AppAPI.startStatsPoller({
        onCallStats: () => dispatch(appActions.maybeConnectAndFetchSessions()),
      });
    });
  };
};

export const fetchFormById = (id, formPrefill) => {
  return async dispatch => {
    try {
      const form = await AppAPI.getChatForm(id);

      if (formPrefill) {
        form.formPrefill = formPrefill;
      }

      dispatch(chatInitForm(form));
      return form;
    } catch (e) {
      return Promise.reject(e);
    }
  };
};

export const handleStartUnreadChecker = () => (dispatch, getState) => {
  let lastUnreadCount;
  setInterval(() => {
    const currentUnreadCount = getTotalUnreadMessageCount(getState());
    if (lastUnreadCount !== currentUnreadCount) {
      lastUnreadCount = currentUnreadCount;
      AppAPI.broadcast('unread', currentUnreadCount);
    }
  }, 500);
};

export const handleWhiteListOptions = whiteListOptions => (dispatch, getState) => {
  if (!isEmpty(whiteListOptions)) {
    helpers.checkWhiteList({ whiteListOptions, getState, dispatch });
  }
  const store = AppAPI.store;
  helpers.addWhiteListListeners({ whiteListOptions, store });
};

export const handleStartChat = cb => {
  return async function startChat(dispatch) {
    const settings = await AppAPI.getCurrentCompanySettings();
    const trackingToken = ls.get('x-kustomer-tracking-token');

    let customerStats;

    if (trackingToken) {
      customerStats = await AppAPI.getCustomerStats();
    }

    const display = settings.offhoursDisplay;
    const isInsideBusinessHours = utils.isNowInsideBillingHours(settings.hours);
    const isNowAHoliday = utils.isNowAHoliday(settings.holidays);
    const lastActivity = get(customerStats, 'activity');
    const shouldMaintainActiveChats = AppAPI.shouldMaintainActiveChats(lastActivity);
    const hideChat = (!isInsideBusinessHours || isNowAHoliday) && display === 'none';

    const isInsideBusinessHoursWithHolidays = isInsideBusinessHours && !isNowAHoliday;

    dispatch(chatUpdateCompany(settings));

    if (!shouldMaintainActiveChats && (!settings.enabled || hideChat)) {
      handleChatIsNotEnabled({ settings, isInsideBusinessHours: isInsideBusinessHoursWithHolidays }, cb);
      return;
    }

    if (settings.activeForm) {
      try {
        const formId = AppAPI.getFormOverride() || settings.activeForm;
        const form = await AppAPI.getChatForm(formId);

        dispatch(chatInitForm(form));
      } catch (e) {
        // noop
      }
    }

    dispatch(chatIsAfterHours(!isInsideBusinessHours && display === 'offline'));
    dispatch(handleAddCompanyUser(settings));

    const campaignsEnabled = get(settings, 'campaignsEnabled');

    if (campaignsEnabled) {
      dispatch(handleStartConnection(lastActivity));
      AppAPI.initClientActivity();
    } else if ((lastActivity || lastActivity !== null) && trackingToken) {
      dispatch(handleStartConnection(lastActivity, true));
      dispatch(handleInitCustomerData({})).then(() => dispatch(handleSingleChat()));
    }

    handleChatIsEnabled({ settings, isInsideBusinessHours: isInsideBusinessHoursWithHolidays }, cb);

    dispatch(handleStartUnreadChecker());
    const domainCriteria = get(settings, 'domainCriteria');
    dispatch(handleWhiteListOptions(domainCriteria));
  };
};

export const getTeams = ids => {
  return async dispatch => {
    try {
      const teams = await AppAPI.getTeams(ids);
      dispatch(handleChatFormTeams(teams));
    } catch (e) {
      console.log(e); // eslint-disable-line no-console
    }
  };
};

export const handleUpdateActiveSessionSeen = () => {
  return (dispatch, getStore) => {
    const state = getStore();
    const chatConversationOpen = chatConversationIsOpen(state);
    const documentHasFocus = document && document.hasFocus && document.hasFocus();
    if (documentHasFocus && chatConversationOpen) {
      const activeSessionId = getActiveSessionId(state);
      dispatch(handleMarkChatSessionSeen(activeSessionId));
    }
  };
};

export const submitForm = data => {
  return async (dispatch, getState) => {
    const state = getState();
    const form = getChatForm(state);
    const isAfterHours = selectIsAfterHours(state);
    const id = get(form, 'id');

    try {
      const postData = await AppAPI.submitForm(id, data);
      const maxAge = 3600; // one hour
      const sessionId = get(postData, 'session.id');

      AppAPI.setActiveSession(sessionId);

      removeCookie('formId');
      if (isAfterHours) {
        saveCookie(`input-disabled-${sessionId}`, sessionId, { maxAge });
      }

      dispatch(chatFormPost(postData));
      dispatch(maybeDismissCampaigns());
      dispatch(volumeControlActions.handleMaybeStart(sessionId, false));
      AppAPI.flushPendingDescribeNexts();

      ensureCustomerId();

      return postData;
    } catch (e) {
      throw e;
    }
  };
};

export const submitVolumeControlForm = data => {
  return async dispatch => {
    try {
      const postData = await AppAPI.submitVCForm(data);
      const maxAge = 3600; // one hour
      const sessionId = get(postData, 'session.id');

      AppAPI.setActiveSession(sessionId);
      saveCookie(`input-disabled-${sessionId}`, sessionId, { maxAge });

      dispatch(chatVolumeControlPost(postData));
      dispatch(chatVolumeControlStop(sessionId));
      dispatch(clearSessionQueueEntities());
      return postData;
    } catch (e) {
      return Promise.reject(e);
    }
  };
};

export const routeToConversation = () => {
  return (dispatch, getState) => {
    const session = getActiveSession(getState());
    return dispatch(routingActions.handleJoinConversation(session));
  };
};

export const decideDisplay = () => (dispatch, getState) => {
  const state = getState();
  const form = getChatForm(state);
  const formInProgress = form.inProgress;
  const company = getCompany(state);
  if (!company.hours) return;
  const { hours, holidays, offhoursDisplay } = company;
  const initialIsAfterHours = selectIsAfterHours(state);

  const isNowAHoliday = utils.isNowAHoliday(holidays);
  const isNowAfterHours = isNowAHoliday || (!isNowAHoliday && !utils.isNowInsideBillingHours(hours));

  if (isNowAfterHours !== initialIsAfterHours) {
    const hide = offhoursDisplay === 'none';

    if (isNowAfterHours && hide && !formInProgress) {
      dispatch(appActions.handleStopAppIfDisconnected());
    }

    if (isNowAfterHours && offhoursDisplay === 'offline') {
      dispatch(chatIsAfterHours(true));
    }

    if (!isNowAfterHours) {
      dispatch(chatIsAfterHours(false));
    }
  }
};

export const chatFormInProgress = inProgress => ({
  type: CHAT_FORM_IN_PROGRESS,
  inProgress,
});

export const handleFailedUpload = failedMessageBody => (dispatch, getState) => {
  const sessionId = getActiveSessionId(getState());
  const message = {
    id: utils.guid(),
    body: failedMessageBody,
    type: 'customer',
    sessionId,
    failed: true,
  };

  const tempMessage = Message(message);

  return dispatch(inputActions.messageSendOptimistically(tempMessage));
};

export const fetchMessageAttachment = (messageId, attachmentId, timeout = 0) => dispatch => {
  return setTimeout(async () => {
    try {
      const attachment = await AppAPI.fetchMessageAttachment(messageId, attachmentId);

      dispatch(chatAttachmentFetch(attachment, messageId));

      return attachment;
    } catch (e) {
      return DEFAULT_OBJECT;
    }
  }, timeout);
};

export const sendMessageWithAttachment = (params = DEFAULT_OBJECT) => {
  const sessionId = params.sessionId;
  const data = {
    body: params.messageBody,
    session: sessionId,
    attachments: [params.attachmentId],
  };

  return AppAPI.createMessageWithAttachment(data);
};

// eslint-disable-next-line max-params
export const uploadAttachment = (attachment, file, messageBody, failedMessage) => async (dispatch, getState) => {
  try {
    const sessionId = getActiveSessionId(getState());
    const attachmentId = attachment.id;
    const fields = attachment.fields;
    const url = attachment.url;
    const formData = utils.createFormData(fields, file);

    await AppAPI.uploadAttachment(url, formData);

    const message = await sendMessageWithAttachment({ sessionId, attachmentId, messageBody });

    dispatch(chatUpdateSessionMessages(sessionId, [message]));
    // we pass a timeout because the initial fetch for this attachment fails :(
    dispatch(fetchMessageAttachment(message.id, attachmentId, 4000));

    return message;
  } catch (e) {
    dispatch(handleFailedUpload(failedMessage));
    throw e;
  }
};

// eslint-disable-next-line max-params
export const createAttachment = (data, file, messageBody, failedMessage) => async dispatch => {
  try {
    const attachment = await AppAPI.createAttachment(data);

    dispatch(chatAttachmentCreate(attachment));
    dispatch(uploadAttachment(attachment, file, messageBody, failedMessage));

    return attachment;
  } catch (e) {
    dispatch(handleFailedUpload(failedMessage));
    throw e;
  }
};

export const fetchQueue = sessionId => async dispatch => {
  try {
    const sessionQueue = await AppAPI.getQueueWaitTime(sessionId);

    dispatch(chatQueueFetch(sessionQueue));

    return sessionQueue;
  } catch (e) {
    throw e;
  }
};

export const maybeTriggerUpfrontVolumeControl = sessionId => (dispatch, getState) => {
  const waitTime = selectSessionWaitTime(getState(), sessionId);
  const threshold = getUpfrontVolumeControlThreshold(getState());
  const upfrontVolumeControlEnabled = getUpfrontVolumeControlEnabled(getState());
  const waitTimeOver = waitTime > threshold;

  const triggerUpfront = upfrontVolumeControlEnabled && waitTimeOver;
  const hasNotTriggered = loadCookie(`vc-${sessionId}`);

  if (triggerUpfront && !hasNotTriggered) {
    saveCookie(`vc-${sessionId}`, sessionId, { maxAge: 43200 }); // maxAge = 12 hours
    dispatch(volumeControlActions.handleMaybeStart(sessionId, triggerUpfront));
  }
};

export const pollForQueue = sessionId => async (dispatch, getState) => {
  const isLocked = isSessionLocked(getState(), sessionId);
  const upfrontVolumeControlEnabled = getUpfrontVolumeControlEnabled(getState());

  if (isLocked || !upfrontVolumeControlEnabled) {
    return Promise.resolve(DEFAULT_OBJECT);
  }

  try {
    const queue = await dispatch(fetchQueue(sessionId));

    dispatch(maybeTriggerUpfrontVolumeControl(sessionId));

    return queue;
  } catch (e) {
    throw e;
  }
};

export const handleFetchChatMessageById = ({ messageId, sessionId }) => dispatch => {
  AppAPI.fetchChatMessageById({ messageId, sessionId })
    .then(messageRes => dispatch(onMessageReceived(messageRes)))
    .catch(error => {
      throw error;
    });
};

export const fetchSessionState = () => {
  return dispatch => {
    if (sessionStateLoaded) return;

    AppAPI.getSessionState().then(sessionStates => {
      dispatch(setSessionState(sessionStates));
      sessionStateLoaded = true;
    });
  };
};

export const updateSessionState = data => {
  return dispatch => {
    AppAPI.updateSessionState(data).then(sessionStates => {
      dispatch(setSessionState(sessionStates));
    });
  };
};

export const updateSessionStateSatisfaction = (sessionId, data) => {
  return (dispatch, getState) => {
    const state = getState();

    const sessionStates = getSessionStates(state);

    const updatedSessionState = merge(sessionStates, {
      [`session_${sessionId}`]: {
        satisfaction: data,
      },
    });

    dispatch(updateSessionState(updatedSessionState));

    dispatch(setSessionState(updatedSessionState));
  };
};

export const removeSessionStateSatisfaction = sessionId => {
  return (dispatch, getState) => {
    const state = getState();

    const sessionStates = getSessionStates(state);

    const updatedSessionState = omit(sessionStates, `session_${sessionId}`);

    dispatch(updateSessionState(updatedSessionState));

    dispatch(setSessionState(updatedSessionState));
  };
};

export const submitSatisfactionResponse = (responseId, data) => {
  return AppAPI.submitSatisfactionResponse(responseId, data);
};

export const setFocus = data => {
  return {
    type: SET_FOCUS,
    data,
  };
};
